"use client";

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Language, getTranslations } from '@/lib/i18n';

interface ReservationCTAProps {
  language: Language;
}

export default function ReservationCTA({ language }: ReservationCTAProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const t = getTranslations(language);
  const r = t.home.reservation;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section
      id="reservation"
      className="py-24 md:py-32 px-6 md:px-12 bg-primary relative overflow-hidden"
    >
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto text-center relative z-10 space-y-12 animate-fade-in">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center">
            <Icon name="CalendarIcon" size={40} className="text-primary-foreground" />
          </div>
        </div>

        {/* Headline */}
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-primary-foreground">
          {r.title} <br />
          <span className="italic font-light">{r.titleItalic}</span>
        </h2>

        {/* Description */}
        <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl mx-auto leading-relaxed">
          {r.description}
        </p>

        {/* Availability Badge */}
        <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20">
          <div className="w-3 h-3 rounded-full bg-success animate-pulse-dot"></div>
          <span className="text-sm font-bold text-primary-foreground uppercase tracking-wider">
            {r.tablesAvailable}
          </span>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8">
          <a
            href="tel:5551234567"
            className="w-full sm:w-auto px-10 py-5 bg-primary-foreground text-primary rounded-full text-base font-bold uppercase tracking-wider hover:scale-105 active:scale-95 transition-transform shadow-2xl flex items-center justify-center gap-3 group"
          >
            <Icon name="PhoneIcon" size={20} />
            <span>{r.callUs}</span>
          </a>
          <button className="w-full sm:w-auto px-10 py-5 border-2 border-primary-foreground text-primary-foreground rounded-full text-base font-bold uppercase tracking-wider hover:bg-primary-foreground hover:text-primary transition-all flex items-center justify-center gap-3">
            <Icon name="CalendarDaysIcon" size={20} />
            <span>{r.bookOnline}</span>
          </button>
        </div>

        {/* Info */}
        <p className="text-sm text-primary-foreground/70 pt-8">{r.hours}</p>
      </div>
    </section>
  );
}